/**
 * client_preview_controller.js — the owner's client preview: the strip and
 * the peak (Stimulus). Markup: templates/quote_public_view.html and
 * templates/invoice_public_view.html, drawn for the owner by
 * application/web/public_quote_page.py and public_invoice_page.py for_owner,
 * and the strip from onboard_client_strip in templates/macros/onboarding.html.
 * Rules: docs/ONBOARDING_DESIGN_RULES.md W7, M5, M6.
 *
 * Once the page is on screen it asks the server whether this is the person's
 * first client view (seeClientView, _onboarding_beacon.js), which records the
 * view; the page itself writes nothing. Only a yes plays the peak, once the
 * page is seen; any other answer, or none, leaves it at rest. The markup never
 * says whether the person has celebrated, so a saved copy of the page cannot
 * play it again. A Turbo preview of a snapshot and a page-check swap repaint
 * a view already asked about, so they ask nothing.
 *
 * Every answered view dispatches `onboard:client-view-seen` on document with
 * { quoteId, invoiceId, via, first } (the one previewed, the other null),
 * for the walkthrough's client-view steps.
 *
 * Accept, Decline, I've paid and the PDF link are inert here (they name no route); a tap
 * swaps the strip's line for what that control says (explain). Turbo's
 * snapshot gets the still check and the first line back.
 */
import { Controller } from "@hotwired/stimulus";
import { playPeak, settlePeak, swapText } from "./_onboard.js";
import { seeClientView } from "./_onboarding_beacon.js";

export default class extends Controller {
    static targets = ["peak", "line", "say"];
    static values = { quote: Number, invoice: Number, via: String, seenUrl: String };

    connect() {
        this._line = this.lineTarget.textContent;
        this._onBeforeCache = () => this._rest();
        document.addEventListener("turbo:before-cache", this._onBeforeCache);
        if (this._repaint()) {
            settlePeak(this.peakTarget);
            return;
        }
        this._ask();
    }

    disconnect() {
        document.removeEventListener("turbo:before-cache", this._onBeforeCache);
        this._stopWaitingToBeSeen();
    }

    /** Swap the strip's line for what the tapped control says. */
    explain({ params: { say } }) {
        const text = this.sayTargets.find((target) => target.dataset.say === say)?.textContent;
        if (text && this.lineTarget.textContent !== text) swapText(this.lineTarget, text);
    }

    _repaint() {
        return document.documentElement.hasAttribute("data-turbo-preview")
            || Boolean(window.chippiePageCheck?.isSwapping());
    }

    async _ask() {
        const via = this.viaValue || null;
        const { recorded, first } = await seeClientView(this.seenUrlValue, via);
        if (!this.element.isConnected) return;
        if (recorded) {
            document.dispatchEvent(new CustomEvent("onboard:client-view-seen",
                { detail: { quoteId: this.quoteValue || null, invoiceId: this.invoiceValue || null, via, first } }));
        }
        if (first) this._whenSeen(() => playPeak(this.peakTarget));
        else settlePeak(this.peakTarget);
    }

    _whenSeen(play) {
        if (document.visibilityState === "visible") {
            play();
            return;
        }
        this._onVisible = () => {
            if (document.visibilityState !== "visible") return;
            this._stopWaitingToBeSeen();
            play();
        };
        document.addEventListener("visibilitychange", this._onVisible);
    }

    _stopWaitingToBeSeen() {
        if (!this._onVisible) return;
        document.removeEventListener("visibilitychange", this._onVisible);
        this._onVisible = null;
    }

    _rest() {
        settlePeak(this.peakTarget);
        this.lineTarget.removeAttribute("data-swapping");
        this.lineTarget.textContent = this._line;
    }
}
